/** True when the element is fully within the current viewport. */
export const isElementInViewport = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewportHeight &&
    rect.right <= viewportWidth
  );
};

/** The data attribute that ties a rendered span back to its page element. */
export const WORD_ID_ATTRIBUTE = "data-word-id";

/**
 * Walks up from an event target to the nearest element carrying an id.
 *
 * Gestures land on whatever is under the pointer — often a character span inside
 * a word — so the id has to be resolved by climbing the tree.
 */
export const resolveElementId = (
  element: Element | null,
  attribute: string = WORD_ID_ATTRIBUTE,
): string | undefined => {
  if (!element) return undefined;
  const own = element.getAttribute?.(attribute);
  if (own) return own;
  return element.closest?.(`[${attribute}]`)?.getAttribute(attribute) ?? undefined;
};

/**
 * Picks a random sample of `fraction` of the given items.
 *
 * Uses a partial Fisher–Yates shuffle: the previous implementation sorted with a
 * random comparator, which is biased and, with an inconsistent comparator, is
 * undefined behaviour in the spec.
 */
export const randomSample = <T>(items: readonly T[], fraction: number): T[] => {
  const pool = [...items];
  const size = Math.floor(Math.max(0, Math.min(1, fraction)) * pool.length);
  for (let i = 0; i < size; i += 1) {
    const j = i + Math.floor(Math.random() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, size);
};
